// src/components/Cases/CaseManagerPanel.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Spinner,
  Table,
  Badge,
  InputGroup,
  Alert,
  Card,
} from "react-bootstrap";
import ICD10PickerModal from "../SearchICD10/SearchICD10";
import { getCasesByPatientId, createCase, updateCaseById } from "../../acases";

/**
 * Props
 * - patientId: string (required)
 * - invoiceICDSelections: Array<{ item, isPrimary }>
 * - existingReferrers: Array<{ id, name, bhf, hpcsa }>
 * - currentUserId: string
 * - onUseIcds: (icdSelections[]) => void
 * - variant: "manager" | "picker" (default "manager")
 * - readOnly: boolean (default false) — disables editing (except New if you allow it)
 * - onPickCase: (caseObj) => void — used in "picker" mode when selecting a case
 * - allowCreateInPicker: boolean (default true) — allow creating in picker variant
 * - showOnlyActiveDefault: boolean (default true) — list hides archived (ended) cases by default
 */
export default function CaseManagerPanel({
  patientId,
  invoiceICDSelections = [],
  existingReferrers = [],
  onUseIcds,
  currentUserId,
  variant = "manager",
  readOnly = false,
  onPickCase,
  allowCreateInPicker = true,
  showOnlyActiveDefault = true,
}) {
  // ----------------- helpers -----------------
  const toArray = (x) => {
    if (Array.isArray(x)) return x;
    if (Array.isArray(x?.cases)) return x.cases;
    if (Array.isArray(x?.data)) return x.data;
    if (Array.isArray(x?.items)) return x.items;
    return [];
  };

  const idOf = (c) => c?._id?.$oid || c?._id || c?.id || null;

  const normalizeToMMDDYYYY = (v) => {
    if (!v) return "";
    if (typeof v === "string") {
      if (/^\d{2}-\d{2}-\d{4}$/.test(v)) return v;
      const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/); // ISO
      if (m) return `${m[2]}-${m[3]}-${m[1]}`;
    }
    if (v && typeof v === "object" && v.$date) {
      const iso = String(v.$date);
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[2]}-${m[3]}-${m[1]}`;
    }
    return "";
  };

  const mmddyyyySortKey = (s) => {
    if (!s) return "";
    const [mm, dd, yyyy] = String(s).split("-");
    if (!mm || !dd || !yyyy) return "";
    return `${yyyy}${mm.padStart(2, "0")}${dd.padStart(2, "0")}`;
  };

  const todayKey = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}${mm}${dd}`;
  };

  const isActiveCase = (startStr, endStr) => {
    const endKey = mmddyyyySortKey(endStr);
    return !endKey || endKey >= todayKey();
  };

  const mmddyyyyToYyyyMmDd = (s) => {
    if (!s) return "";
    const [mm, dd, yyyy] = String(s).split("-");
    if (!mm || !dd || !yyyy) return "";
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  };

  const yyyyMmDdToMmDdYyyy = (s) => {
    if (!s) return "";
    const [yyyy, mm, dd] = String(s).split("-");
    if (!yyyy || !mm || !dd) return "";
    return `${mm.padStart(2, "0")}-${dd.padStart(2, "0")}-${yyyy}`;
  };

  const codeOf = useCallback(
    (obj) => obj?.ICD10_Code || obj?.ICD10_3_Code || "",
    []
  );

  // ----------------- state -----------------
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cases, setCases] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [onlyActive, setOnlyActive] = useState(showOnlyActiveDefault);

  const [useExistingReferrer, setUseExistingReferrer] = useState(true);
  const [selectedReferrerId, setSelectedReferrerId] = useState(
    existingReferrers?.[0]?.id || ""
  );
  const [refName, setRefName] = useState("");
  const [refBHF, setRefBHF] = useState("");
  const [refHPCSA, setRefHPCSA] = useState("");

  const [caseDescription, setCaseDescription] = useState("");
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [authorizationNumber, setAuthorizationNumber] = useState("");
  const [caseStartDate, setCaseStartDate] = useState("");
  const [caseEndDate, setCaseEndDate] = useState("");
  const [caseMaxSessions, setCaseMaxSessions] = useState("");

  const [showICDModal, setShowICDModal] = useState(false);
  const [icdSelections, setIcdSelections] = useState(() =>
    (invoiceICDSelections || []).map((s) => ({
      item: s.item ?? s,
      isPrimary: !!s.isPrimary,
    }))
  );

  const ROW_ACTIVE = "#CFE2FF";
  const ROW_INACTIVE = "#f8f9fa";
  const newModeInitialized = useRef(false);

  const primaryICDs = useMemo(
    () => icdSelections.filter((s) => s.isPrimary).map((s) => s.item),
    [icdSelections]
  );
  const secondaryICDs = useMemo(
    () => icdSelections.filter((s) => !s.isPrimary).map((s) => s.item),
    [icdSelections]
  );

  const icd101 = useMemo(
    () => codeOf(primaryICDs[0]) || "",
    [primaryICDs, codeOf]
  );
  const icd102 = useMemo(
    () => secondaryICDs.map((i) => codeOf(i)).filter(Boolean),
    [secondaryICDs, codeOf]
  );

  const selectedCase = useMemo(
    () => toArray(cases).find((c) => idOf(c) === selectedCaseId) || null,
    [cases, selectedCaseId]
  );

  // Can user edit right panel?
  const canEdit =
    !readOnly &&
    (variant === "manager" || (variant === "picker" && mode === "new"));

  // ----------------- effects -----------------
useEffect(() => {
  let mounted = true;
  async function load() {
    if (!patientId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getCasesByPatientId(patientId);
      const mapped = toArray(data).map((c) => {
        const startStr = normalizeToMMDDYYYY(c.caseStartDate);
        const endStr = normalizeToMMDDYYYY(c.caseEndDate);
        return { ...c, _startStr: startStr, _endStr: endStr, _normId: idOf(c) };
      });

      const filtered = onlyActive
        ? mapped.filter((c) => isActiveCase(c._startStr, c._endStr))
        : mapped;

      const arr = filtered
        .slice()
        .sort((a, b) =>
          mmddyyyySortKey(b._startStr).localeCompare(mmddyyyySortKey(a._startStr))
        );

      if (mounted) {
        setCases(arr);
        if (arr.length === 0) {
          setSelectedCaseId(null);
          setMode("new");
        } else {
          // 👇 Only force "existing" if we're NOT currently in "new"
          if (mode !== "new") {
            if (!selectedCaseId) setSelectedCaseId(idOf(arr[0]));
            setMode("existing");
          }
        }
      }
    } catch (e) {
      if (mounted) setError(e?.message || "Failed to load cases");
    } finally {
      if (mounted) setLoading(false);
    }
  }
  load();
  return () => {
    mounted = false;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [patientId, onlyActive]); // keep deps as-is; we rely on the mode guard above


  // Hydrate form when switching to an existing case
  useEffect(() => {
    if (mode !== "existing" || !selectedCase) return;

    setRefName(selectedCase.referringDoctorName || "");
    setRefBHF(selectedCase.referringDoctorBHF || "");
    setRefHPCSA(selectedCase.referringDoctorHPCSA || "");
    setCaseDescription(selectedCase.caseDescription || "");
    setAuthorizedBy(selectedCase.authorizedBy || "");
    setAuthorizationNumber(selectedCase.authorizationNumber || "");
    setCaseStartDate(normalizeToMMDDYYYY(selectedCase.caseStartDate));
    setCaseEndDate(normalizeToMMDDYYYY(selectedCase.caseEndDate));
    setCaseMaxSessions(selectedCase.caseMaxSessions || "");

    if (Array.isArray(selectedCase.icdSelections) && selectedCase.icdSelections.length) {
      setIcdSelections(
        selectedCase.icdSelections.map((s) => ({
          item: s.item ?? s,
          isPrimary: !!s.isPrimary,
        }))
      );
    } else {
      const prim = selectedCase.icd101
        ? [{ item: { ICD10_Code: selectedCase.icd101 }, isPrimary: true }]
        : [];
      const secs = toArray(selectedCase.icd102).map((code) => ({
        item: { ICD10_Code: code },
        isPrimary: false,
      }));
      setIcdSelections([...prim, ...secs]);
    }

    const matched = toArray(existingReferrers).find(
      (r) =>
        (selectedCase.referringDoctorHPCSA &&
          r.hpcsa === selectedCase.referringDoctorHPCSA) ||
        (selectedCase.referringDoctorBHF && r.bhf === selectedCase.referringDoctorBHF)
    );
    if (matched) {
      setUseExistingReferrer(true);
      setSelectedReferrerId(matched.id || "");
    } else {
      setUseExistingReferrer(false);
      setSelectedReferrerId("");
    }
  }, [mode, selectedCase, existingReferrers]);

  // Reset fields for a new case
  useEffect(() => {
  // leaving "new" -> allow re-init next time
  if (mode !== "new") {
    newModeInitialized.current = false;
    return;
  }

  // already initialized this entry into "new"
  if (newModeInitialized.current) return;
  newModeInitialized.current = true;

  setSelectedCaseId(null);

  // Only set a default once; if there are referrers, start in "existing referrer" mode,
  // otherwise start in "create new referrer" mode.
  const hasReferrers = toArray(existingReferrers).length > 0;
  setUseExistingReferrer(hasReferrers);
  setSelectedReferrerId(hasReferrers ? toArray(existingReferrers)?.[0]?.id || "" : "");

  setRefName("");
  setRefBHF("");
  setRefHPCSA("");
  setCaseDescription("");
  setAuthorizedBy("");
  setAuthorizationNumber("");
  setCaseStartDate("");
  setCaseEndDate("");
  setCaseMaxSessions("");

  // Seed ICDs from invoice selections (only once on entry to "new")
  setIcdSelections(
    toArray(invoiceICDSelections).map((s) => ({
      item: s.item ?? s,
      isPrimary: !!s.isPrimary,
    }))
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [mode]);

  // Keep referrer details synced when choosing an existing referrer
  useEffect(() => {
    if (!useExistingReferrer) return;
    const r = toArray(existingReferrers).find((x) => x.id === selectedReferrerId);
    if (r) {
      setRefName(r.name || "");
      setRefBHF(r.bhf || "");
      setRefHPCSA(r.hpcsa || "");
    } else {
      setRefName("");
      setRefBHF("");
      setRefHPCSA("");
    }
  }, [useExistingReferrer, selectedReferrerId, existingReferrers]);

  // ----------------- actions -----------------
  const startNewCase = () => {
    if (variant === "picker" && !allowCreateInPicker) return;
    setMode("new");
  };

  const refreshCases = async (selectId) => {
    if (!patientId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getCasesByPatientId(patientId);
      const mapped = toArray(data).map((c) => {
        const startStr = normalizeToMMDDYYYY(c.caseStartDate);
        const endStr = normalizeToMMDDYYYY(c.caseEndDate);
        return { ...c, _startStr: startStr, _endStr: endStr, _normId: idOf(c) };
      });

      const filtered = onlyActive
        ? mapped.filter((c) => isActiveCase(c._startStr, c._endStr))
        : mapped;

      const arr = filtered
        .slice()
        .sort((a, b) =>
          mmddyyyySortKey(b._startStr).localeCompare(mmddyyyySortKey(a._startStr))
        );

      setCases(arr);
      if (arr.length === 0) {
        setSelectedCaseId(null);
        setMode(
          variant === "manager" || (variant === "picker" && allowCreateInPicker)
            ? "new"
            : "existing"
        );
      } else if (selectId) {
        setSelectedCaseId(selectId);
        setMode("existing");
      } else if (!selectedCaseId) {
        setSelectedCaseId(idOf(arr[0]));
        setMode("existing");
      }
    } catch (e) {
      setError(e?.message || "Failed to refresh cases");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;
    if (!patientId) return alert("Patient is required.");
    if (!icd101) return alert("Please select at least one primary ICD-10 code.");

    const payload = {
      patientId,
      referringDoctorName: refName || "",
      referringDoctorBHF: refBHF || "",
      referringDoctorHPCSA: refHPCSA || "",
      icd101,
      icd102,
      icdSelections,
      caseDescription: caseDescription || "",
      authorizedBy: authorizedBy || "",
      authorizationNumber: authorizationNumber || "",
      // keep string dates
      caseStartDate: caseStartDate || "",
      caseEndDate: caseEndDate || "",
      caseMaxSessions: caseMaxSessions || "",
      caseUpdatedBy: currentUserId || "",
    };

    try {
      if (mode === "new" || !selectedCase?._id) {
        const created = await createCase({
          ...payload,
          caseCreatedBy: currentUserId || "",
        });
        await refreshCases(created?._id);
      } else if (selectedCase?._id) {
        await updateCaseById(selectedCase._id, payload);
        await refreshCases(selectedCase._id);
      }
    } catch (e) {
      console.error("Failed to save case:", e);
      alert(e?.message || "Failed to save case");
    }
  };

  const handleUseICDs = () => onUseIcds?.(icdSelections);

  const handlePick = () => {
    if (!onPickCase || !selectedCase) return;
    onPickCase(selectedCase);
  };

  // ----------------- UI bits -----------------
  const LeftList = () => (
    <div className="rounded p-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        {loading ? (
          <span className="d-inline-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" /> <span>Loading</span>
          </span>
        ) : (
          <div className="small text-muted">
            Showing {onlyActive ? "active" : "all"} cases
          </div>
        )}

        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setOnlyActive((v) => !v)}
          >
            {onlyActive ? "Show All" : "Hide Archived"}
          </Button>

          {(variant === "manager" || (variant === "picker" && allowCreateInPicker)) && (
            <Button variant="primary" size="sm" onClick={startNewCase} disabled={readOnly}>
              + New Case
            </Button>
          )}
        </div>
      </div>

      {!toArray(cases).length && !loading ? (
        <div className="text-muted">No cases found.</div>
      ) : (
        <Table hover size="sm" responsive className="mb-0">
          <tbody className="border">
            {toArray(cases).map((c) => {
              const isActiveRow = idOf(c) === selectedCaseId;
              return (
                <tr
                  key={idOf(c)}
                  onClick={() => {
                    setSelectedCaseId(idOf(c));
                    setMode("existing");
                  }}
                  style={{
                    cursor: "pointer",
                    backgroundColor: isActiveRow ? ROW_ACTIVE : ROW_INACTIVE,
                  }}
                >
                  <td
                    style={{
                      backgroundColor: isActiveRow ? ROW_ACTIVE : ROW_INACTIVE,
                    }}
                  >
                    <div className="fw-semibold text-primary">
                      {c?.caseDescription || "Untitled Case"}
                    </div>
                    <div className="small text-muted">
                      {c?._startStr || "-"} {c?._endStr ? `→ ${c._endStr}` : ""}
                    </div>
                    <div className="small text-muted">
                      Ref: {c?.referringDoctorName || "-"} | BHF {c?.referringDoctorBHF || "-"} | HPCSA{" "}
                      {c?.referringDoctorHPCSA || "-"}
                    </div>
                    <div className="mt-1">
                      {c?.icd101 && (
                        <Badge bg="secondary" className="me-1">
                          {c.icd101}
                        </Badge>
                      )}
                      {toArray(c?.icd102)
                        .slice(0, 2)
                        .map((s, i) => (
                          <Badge key={`${idOf(c)}-sec-${i}`} bg="light" text="dark" className="me-1">
                            {s}
                          </Badge>
                        ))}
                      {toArray(c?.icd102).length > 2 && (
                        <span className="text-muted">+{toArray(c?.icd102).length - 2}</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );

  const ReadOnlyView = () => (
    <Card className="border rounded">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h6 className="text-primary mb-1">
              {selectedCase?.caseDescription?.trim() || "Untitled Case"}
            </h6>
            <div className="text-muted small mb-2">
              {selectedCase?._startStr || "—"}
              {selectedCase?._endStr ? ` → ${selectedCase._endStr}` : ""}
            </div>
          </div>

          {variant === "picker" && (
            <div className="text-end">
              <Button size="sm" variant="outline-primary" onClick={handlePick} disabled={!selectedCase}>
                Use This Case
              </Button>
            </div>
          )}
        </div>

        <hr />

        <Row className="mb-2">
          <Col md={6}>
            <div className="small text-muted">Referring Doctor</div>
            <div>{selectedCase?.referringDoctorName || "—"}</div>
            <div className="small text-muted">
              BHF: {selectedCase?.referringDoctorBHF || "—"} &nbsp;|&nbsp; HPCSA:{" "}
              {selectedCase?.referringDoctorHPCSA || "—"}
            </div>
          </Col>
          <Col md={6}>
            <div className="small text-muted">Authorization</div>
            <div>
              {selectedCase?.authorizedBy || "—"}
              {selectedCase?.authorizationNumber ? ` (${selectedCase.authorizationNumber})` : ""}
            </div>
          </Col>
        </Row>

        <div className="mb-2">
          <div className="small text-muted mb-1">ICD-10</div>
          {selectedCase?.icd101 && (
            <Badge bg="secondary" className="me-1">
              {selectedCase.icd101}
            </Badge>
          )}
          {toArray(selectedCase?.icd102).map((code, i) => (
            <Badge key={`sel-sec-${i}`} bg="light" text="dark" className="me-1">
              {code}
            </Badge>
          ))}
        </div>
      </Card.Body>
    </Card>
  );

  // ----------------- render -----------------
  return (
    <div className="p-2">
      <Row className="align-items-center mb-2">
        <Col>
          <h6 className="text-primary mt-1 fw-normal">PATIENT CASES</h6>
        </Col>
      </Row>

      {error && (
        <Row className="mb-2">
          <Col>
            <Alert variant="danger" className="py-2 mb-0">
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="g-3">
        <Col md={5}>
          <LeftList />
        </Col>

        <Col md={7}>
          {/* Right side: either read-only or editable form */}
          {mode === "existing" && selectedCase && !canEdit ? (
            <ReadOnlyView />
          ) : (
            <div className="border rounded p-3">
              <h6 className="text-primary mb-3">
                {mode === "existing" ? (canEdit ? "Edit Case" : "View Case") : "New Case"}
              </h6>

              <Row className="mb-2">
                <Col>
                  <Form.Check
                    type="switch"
                    id="ref-switch"
                    label={
                      useExistingReferrer ? "Using existing referrer" : "Create new referrer"
                    }
                    checked={useExistingReferrer}
                    onChange={(e) => setUseExistingReferrer(e.target.checked)}
                    disabled={!canEdit}
                  />
                </Col>
              </Row>

              {useExistingReferrer ? (
                <>
                  <Form.Group className="mb-2">
                    <Form.Label>Referring Doctor</Form.Label>
                    <Form.Select
                      value={selectedReferrerId}
                      onChange={(e) => setSelectedReferrerId(e.target.value)}
                      disabled={!canEdit}
                    >
                      {toArray(existingReferrers).length === 0 && (
                        <option value="">No referrers found</option>
                      )}
                      {toArray(existingReferrers).map((r) => (
                        <option key={r.id || r.hpcsa || r.bhf} value={r.id}>
                          {r.name || "-"} {r.bhf ? `| BHF ${r.bhf}` : ""}{" "}
                          {r.hpcsa ? `| HPCSA ${r.hpcsa}` : ""}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>BHF</Form.Label>
                        <Form.Control value={refBHF} readOnly />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>HPCSA</Form.Label>
                        <Form.Control value={refHPCSA} readOnly />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Referrer Name</Form.Label>
                        <Form.Control
                          value={refName}
                          onChange={(e) => setRefName(e.target.value)}
                          placeholder="Dr Jane Doe"
                          disabled={!canEdit}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>BHF</Form.Label>
                        <Form.Control
                          value={refBHF}
                          onChange={(e) => setRefBHF(e.target.value)}
                          placeholder="e.g. 0123456"
                          disabled={!canEdit}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>HPCSA</Form.Label>
                        <Form.Control
                          value={refHPCSA}
                          onChange={(e) => setRefHPCSA(e.target.value)}
                          placeholder="e.g. MP0123456"
                          disabled={!canEdit}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}

              <Form.Group className="mb-2">
                <Form.Label>Case Description</Form.Label>
                <Form.Control
                  value={caseDescription}
                  onChange={(e) => setCaseDescription(e.target.value)}
                  placeholder="Brief description of the case"
                  disabled={!canEdit}
                />
              </Form.Group>

              <Row className="mb-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Authorized By</Form.Label>
                    <Form.Control
                      value={authorizedBy}
                      onChange={(e) => setAuthorizedBy(e.target.value)}
                      placeholder="Name of authorizer"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Authorization Number</Form.Label>
                    <Form.Control
                      value={authorizationNumber}
                      onChange={(e) => setAuthorizationNumber(e.target.value)}
                      placeholder="Auth number"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={mmddyyyyToYyyyMmDd(caseStartDate)}
                      onChange={(e) => setCaseStartDate(yyyyMmDdToMmDdYyyy(e.target.value))}
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={mmddyyyyToYyyyMmDd(caseEndDate)}
                      onChange={(e) => setCaseEndDate(yyyyMmDdToMmDdYyyy(e.target.value))}
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Max Sessions</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min={0}
                        step={1}
                        value={caseMaxSessions}
                        onChange={(e) => setCaseMaxSessions(e.target.value)}
                        disabled={!canEdit}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                <h6 className="m-0">ICD-10 Codes</h6>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setIcdSelections([])}
                    disabled={!canEdit || !icdSelections.length}
                  >
                    Clear
                  </Button>
                  <Button variant="primary" onClick={() => setShowICDModal(true)} disabled={!canEdit}>
                    Add / Edit ICD-10
                  </Button>
                </div>
              </div>
              <div className="border rounded p-2">
                {(() => {
                  if (!toArray(icdSelections).length) {
                    return <div className="text-muted">No ICD-10 codes selected.</div>;
                  }
                  const prim = primaryICDs.map(codeOf).filter(Boolean);
                  const sec = icd102;
                  return (
                    <>
                      <div className="mb-1">
                        <strong>Primary</strong>: {prim.length ? prim.join(", ") : "-"}
                      </div>
                      <div>
                        <strong>Secondary</strong>: {sec.length ? sec.join(", ") : "-"}
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="d-flex justify-content-end gap-2 mt-3">
                {mode === "existing" && selectedCase && variant !== "picker" && (
                  <Button variant="outline-primary" onClick={handleUseICDs} disabled={!canEdit}>
                    Use ICDs in Invoice
                  </Button>
                )}

                {/* In picker mode, show a "Use" button even on edit form if you permit editing */}
                {variant === "picker" && mode === "existing" && (
                  <Button variant="outline-primary" onClick={handlePick} disabled={!selectedCase}>
                    Use This Case
                  </Button>
                )}

                {canEdit && (
                  <Button variant="primary" onClick={handleSave} disabled={!patientId || !icd101}>
                    {mode === "new" ? "Create Case" : "Save Changes"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Col>
      </Row>

      <ICD10PickerModal
        show={showICDModal}
        onHide={() => setShowICDModal(false)}
        initialSelected={icdSelections}
        onConfirm={(sel) => {
          setIcdSelections(sel.map((s) => ({ item: s.item ?? s, isPrimary: !!s.isPrimary })));
          setShowICDModal(false);
        }}
      />
    </div>
  );
}
